import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import type { ImportFormat, ParsedFile, RawRow } from './types';

/**
 * Parse an uploaded onboarding file (JSON / CSV / XLSX) into a uniform
 * `{ headers, rows }` shape. Every cell is coerced to a trimmed string —
 * type-aware coercion (numbers, enums) happens later per target field, so
 * the parse layer stays dumb and format-agnostic.
 *
 * <p>
 * For XLSX, pass `sheet` to read a specific tab; omit it to read the first.
 * `sheetNames` is always returned so the caller can offer a sheet picker.
 * </p>
 */
export async function parseImportFile(file: File, sheet?: string): Promise<ParsedFile> {
  const format = detectFormat(file.name);
  if (!format) {
    throw new Error(
      `Unsupported file type "${file.name}". Use a .json, .csv, .xls or .xlsx file.`,
    );
  }

  if (format === 'json') return parseJson(file);
  if (format === 'csv') return parseCsv(file);
  return parseXlsx(file, sheet);
}

/** Map a filename extension to a supported format, or null if unknown. */
export function detectFormat(fileName: string): ImportFormat | null {
  const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
  if (ext === 'json') return 'json';
  if (ext === 'csv') return 'csv';
  if (ext === 'xlsx' || ext === 'xls') return 'xlsx';
  return null;
}

// ── JSON ────────────────────────────────────────────────────────────────────

async function parseJson(file: File): Promise<ParsedFile> {
  const text = await file.text();
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (err) {
    throw new Error(`Could not parse JSON: ${err instanceof Error ? err.message : 'invalid JSON'}`);
  }

  // Accept either a bare array, or an object wrapping a single array property
  // (common in API-style exports, e.g. { "customers": [ … ] }).
  const arr = coerceToArray(parsed);
  if (!arr) {
    throw new Error('Expected a JSON array of rows (or an object wrapping one array).');
  }

  const rows = arr.map(toRawRow);
  return { format: 'json', fileName: file.name, headers: unionHeaders(rows), rows };
}

function coerceToArray(parsed: unknown): unknown[] | null {
  if (Array.isArray(parsed)) return parsed;
  if (parsed && typeof parsed === 'object') {
    const arrays = Object.values(parsed as Record<string, unknown>).filter(Array.isArray);
    if (arrays.length === 1) return arrays[0] as unknown[];
  }
  return null;
}

// ── CSV ───────────────────────────────────────────────────────────────────--

async function parseCsv(file: File): Promise<ParsedFile> {
  const text = await file.text();
  const result = Papa.parse<Record<string, unknown>>(text, {
    header: true,
    skipEmptyLines: 'greedy',
    transformHeader: (h) => h.trim(),
  });
  if (result.errors.length > 0) {
    // Surface the first parse error but don't abort — papaparse recovers most.
    const e = result.errors[0];
    if (e && result.data.length === 0) {
      throw new Error(`CSV parse error (row ${e.row ?? '?'}): ${e.message}`);
    }
  }
  const rows = result.data.map(toRawRow);
  const headers = result.meta.fields?.map((f) => f.trim()).filter(Boolean) ?? unionHeaders(rows);
  return { format: 'csv', fileName: file.name, headers, rows };
}

// ── XLSX ──────────────────────────────────────────────────────────────────--

async function parseXlsx(file: File, sheet?: string): Promise<ParsedFile> {
  const buf = await file.arrayBuffer();
  // `cellDates:false` + `raw:false` below → dates/numbers come back as the
  // formatted display strings, matching how the operator sees them in Excel.
  const wb = XLSX.read(buf, { type: 'array' });
  const sheetNames = wb.SheetNames;
  if (sheetNames.length === 0) throw new Error('The workbook has no sheets.');

  const activeSheet = sheet && sheetNames.includes(sheet) ? sheet : sheetNames[0]!;
  const ws = wb.Sheets[activeSheet]!;
  const json = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws, {
    defval: '',
    raw: false,
    blankrows: false,
  });
  const rows = json.map(toRawRow);
  return {
    format: 'xlsx',
    fileName: file.name,
    headers: unionHeaders(rows),
    rows,
    sheetNames,
    activeSheet,
  };
}

// ── Shared helpers ────────────────────────────────────────────────────────--

/** Coerce one source object into a string-only row. Skips nested
 *  objects/arrays (flat exports only); scalars become trimmed strings. */
function toRawRow(obj: unknown): RawRow {
  const row: RawRow = {};
  if (!obj || typeof obj !== 'object') return row;
  for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
    const k = key.trim();
    if (!k) continue;
    if (value == null) {
      row[k] = '';
    } else if (typeof value === 'object') {
      continue; // skip nested structures — v1 handles flat rows only
    } else {
      row[k] = String(value).trim();
    }
  }
  return row;
}

/** Union of keys across all rows, preserving first-seen order. */
function unionHeaders(rows: RawRow[]): string[] {
  const seen = new Set<string>();
  const order: string[] = [];
  for (const row of rows) {
    for (const key of Object.keys(row)) {
      if (!seen.has(key)) {
        seen.add(key);
        order.push(key);
      }
    }
  }
  return order;
}
